import React, { useMemo, } from 'react';
import { collection, query, where, getCountFromServer, getDocs, } from 'firebase/firestore';
import { startOfDay, endOfDay, addDays, } from 'date-fns';
import { useAsync, } from 'react-use';
import { sumBy, sortBy, fromPairs, } from 'lodash';

import { db } from '../../firebase';
import { routeObjs } from '../../routes';
import useQueryParams from '../hooks/useQueryParams';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import AdminPage from '../AdminPage';
import QueryDateRangeSelector from '../QueryDateRangeSelector';
import QuerySelector from '../QuerySelector';
import QueryBoolean from '../QueryBoolean';
import OverlayLoading from '../OverlayLoading';

export default function AdminAnalytics() {
  return (
    <AdminPage>
      <PageContent />
    </AdminPage>
  );
}

function PageContent(props) {
  const queryParams = useQueryParams();

  const defaultStartDate = addDays(new Date(), -30);
  const defaultEndDate = new Date();
  const dateRange = useMemo(() => queryParams.dateRange || [defaultStartDate, defaultEndDate], [queryParams.dateRange?.[0], queryParams.dateRange?.[1]]);
  const startDate = useMemo(() => dateRange[0] ? new Date(dateRange[0]) : defaultStartDate, [dateRange]);
  const endDate = useMemo(() => dateRange[1] ? new Date(dateRange[1]) : defaultEndDate, [dateRange]);

  const tenants = useCollectionSubscription(query(collection(db, 'tenants')));
  const selectedTenantId = queryParams.tenantId;
  const includeAdmin = queryParams.includeAdmin === '1';

  const pages = useMemo(() => routeObjs.filter(_ => _.label), []);

  const { loading, value: { pageStats = [], totalCount = 0, tenantStats = [] } = {} } = useAsync(async () => {
    const stats = await Promise.all(
      pages.map(async ({ path, label }) => {
        const pathQuery = query(
          collection(db, 'pageViews'),
          ...(selectedTenantId ? [where('tenantId', '==', selectedTenantId)] : []),
          ...(!includeAdmin ? [where('isAdmin', '==', false)] : []),
          where('path', '==', path),
          where('createdAt', '>=', startOfDay(startDate)),
          where('createdAt', '<=', endOfDay(endDate))
        );

        const countSnapshot = await getCountFromServer(pathQuery);
        const count = countSnapshot.data().count;

        return {
          path,
          label,
          count,
        };
      })
    );

    const filteredStats = stats.filter(_ => _.count > 0);
    const sortedStats = sortBy(filteredStats, _ => -_.count);
    const totalCount = sumBy(stats, 'count');

    const tenantStatsArray = await Promise.all(
      tenants.map(async (tenant) => {
        const tenantQuery = query(
          collection(db, 'pageViews'),
          where('tenantId', '==', tenant.id),
          ...(!includeAdmin ? [where('isAdmin', '==', false)] : []),
          where('createdAt', '>=', startOfDay(startDate)),
          where('createdAt', '<=', endOfDay(endDate))
        );

        const countSnapshot = await getCountFromServer(tenantQuery);
        const count = countSnapshot.data().count;

        return {
          tenantId: tenant.id,
          tenantName: tenant.name,
          count,
        };
      })
    );

    const filteredTenantStats = tenantStatsArray.filter(_ => _.count > 0);
    const sortedTenantStats = sortBy(filteredTenantStats, _ => -_.count);

    return { pageStats: sortedStats, totalCount, tenantStats: sortedTenantStats };
  }, [startDate, endDate, selectedTenantId, includeAdmin, pages, tenants]);

  return (
    <div className="admin-analytics container-fluid">
      <div className="p-4 bg-white my-4">
        <div className="d-flex justify-content-center mb-3">
          <h4>利用状況分析</h4>
        </div>

        <div className="mb-4 d-flex gap-3 align-items-end">
          <QueryDateRangeSelector
            paramName="dateRange"
            label="期間"
            defaultValue={[startDate, endDate]}
          />
          <QueryBoolean
            paramName="includeAdmin"
            label="adminも含む"
            defaultValue="0"
          />
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <h5>総PV数: {totalCount.toLocaleString()}</h5>
            </div>

            <div className="mb-5">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">ページ別アクセス数</h5>
                <QuerySelector
                  paramName="tenantId"
                  label="テナント"
                  options={tenants.map(_ => ({ value: _.id, label: _.name }))}
                  width={200}
                />
              </div>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead className="table-light">
                    <tr>
                      <th>ページ</th>
                      <th>パス</th>
                      <th className="text-end">PV数</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageStats.map(_ => (
                      <tr key={_.path}>
                        <td>{_.label}</td>
                        <td>{_.path}</td>
                        <td className="text-end">{_.count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-5">
              <h5 className="mb-3">テナント別アクセス数</h5>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead className="table-light">
                    <tr>
                      <th>テナント名</th>
                      <th className="text-end">PV数</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenantStats.map(_ => (
                      <tr key={_.tenantId}>
                        <td>{_.tenantName}</td>
                        <td className="text-end">{_.count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
